import { addCalendarMonths } from './calendarMonths';

/**
 * Offer pricing: what the plaintiff owes, and when.
 *
 * A repayment row is a BAND — one amount owed anywhere inside a window — not
 * a point on a date. That is what the market's paper contracts do, and the
 * only reading under which settling between rows has a defined answer.
 *
 * This is the browser's half. backend/offer_pricing.py is the stored half,
 * and the two are pinned to the same scenarios in
 * src/data/offerPricingContract.json — because this calculation has already
 * been implemented twice and disagreed three times in production.
 *
 * Parameters are camelCase, as everywhere else here. Band object KEYS are
 * snake_case, matching the stored document exactly, so one set of fixture
 * keys asserts against both sides with no translation layer to get wrong.
 *
 * The fixture pins what it enumerates and NOTHING ELSE. It does not make
 * divergence impossible; it makes an enumerated divergence loud. Every shared
 * rule that is not a fixture row has to be held in step by hand, and the one
 * that bit us is ROUNDING MODE: this half has always rounded money half-up,
 * while offer_pricing.py used Python's built-in `round`, which is banker's
 * rounding (ties-to-even). They agree everywhere except on an exact
 * half-cent, where they differ by a penny — about 1 offer in 400 had a
 * payment window whose amount the funder approved and the plaintiff would
 * never see. Both sides are half-up now, and offerPricingContract.json
 * carries a scenario landing on an exact half-cent so the rule is enumerated
 * rather than remembered. If you change round2, change
 * offer_pricing._round_half_up in the same commit.
 */

export const PRICING_VERSION = 2;

export const VALID_INTERVALS = [1, 3, 6];
export const DEFAULT_INTERVAL = 6;

export const VALID_FEE_MODELS = ['itemized', 'flat'];
export const DEFAULT_FEE_MODEL = 'itemized';

/**
 * Today's behaviour: fees ride the balance and accrue interest alongside
 * the advance. A named constant, not a literal `true` at the call site, for
 * the same reason DEFAULT_INTERVAL and DEFAULT_FEE_MODEL are — it is what
 * backend/offer_pricing.py's DEFAULT_FEES_ACCRUE pins against.
 */
export const DEFAULT_FEES_ACCRUE = true;

export const DEFAULT_BROKER_FEE_PERCENTAGE = 15;
export const DEFAULT_APPLICATION_FEE = 99;
/**
 * Signed off at $100 (2026-09-11). It applies ONLY when a funder has chosen the
 * flat fee model and set no processing fee of their own, so it is a floor for
 * the unconfigured case rather than a rate anyone is quoted. It is deliberately
 * below the $250 seen on the market contracts this was modelled on.
 *
 * Like the estimator rates in src/data/funderRates.js, changing it is a pricing
 * decision, not a refactor. Mirrors backend/offer_pricing.py.
 */
export const DEFAULT_PROCESSING_FEE = 100;

/**
 * Money rounds HALF-UP. `Math.round` is already half-up (it rounds ties
 * toward +Infinity), so this is the plain form; the twin in
 * backend/offer_pricing.py has to say it explicitly because Python's `round`
 * is not. Both are exercised on an exact half-cent by the
 * `half_cent_rounding` scenario in the shared contract fixture.
 */
const round2 = (n) => Math.round(n * 100) / 100;

/**
 * The day before a `YYYY-MM-DD` date. Bands are contiguous, so a band ends
 * the day before the next one starts.
 *
 * Built and read in LOCAL components, like calendarMonths.js: a funding date
 * is a plain calendar date with no time and no zone, and anchoring at an
 * instant is what makes a boundary render a day early further west.
 */
const previousDay = (isoDate) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(isoDate || '').trim());
  if (!match) return null;
  const [, year, month, day] = match;
  // Day 0 of a month is the last day of the one before, so this rolls back
  // across month and year boundaries without a special case.
  const shifted = new Date(Number(year), Number(month) - 1, Number(day) - 1);
  const yyyy = String(shifted.getFullYear()).padStart(4, '0');
  const mm = String(shifted.getMonth() + 1).padStart(2, '0');
  const dd = String(shifted.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

/**
 * A submitted fee counts only behind its explicit `<field>_overridden` flag.
 * The form sends every fee on every save, so reading one unconditionally
 * turns a stale value into a silent change to the terms.
 */
const override = (submitted, field, fallback) => (
  submitted[`${field}_overridden`]
    ? Number(submitted[field] || 0)
    : fallback
);

export const resolveFees = ({ advance, feeModel, funder, submitted, platformFee }) => {
  if (!VALID_FEE_MODELS.includes(feeModel)) {
    throw new Error(`feeModel must be one of ${VALID_FEE_MODELS.join(', ')}, got ${feeModel}`);
  }

  const settings = funder || {};
  const form = submitted || {};

  let brokerFee = 0;
  let applicationFee = 0;
  let processingFee = 0;

  if (feeModel === 'itemized') {
    const pct = settings.broker_fee_percentage ?? DEFAULT_BROKER_FEE_PERCENTAGE;
    brokerFee = override(form, 'broker_fee', advance * (Number(pct) / 100));
    // The cap binds either way — an admin can set it too, so a per-offer
    // override must not be a way around it.
    if (settings.broker_fee_cap !== null && settings.broker_fee_cap !== undefined) {
      brokerFee = Math.min(brokerFee, Number(settings.broker_fee_cap));
    }
    applicationFee = override(
      form, 'application_fee',
      Number(settings.application_fee ?? DEFAULT_APPLICATION_FEE),
    );
  } else {
    processingFee = override(
      form, 'processing_fee',
      Number(settings.processing_fee ?? DEFAULT_PROCESSING_FEE),
    );
  }

  brokerFee = round2(brokerFee);
  applicationFee = round2(applicationFee);
  processingFee = round2(processingFee);
  const funderFees = round2(brokerFee + applicationFee + processingFee);

  return {
    broker_fee: brokerFee,
    application_fee: applicationFee,
    processing_fee: processingFee,
    funder_fees: funderFees,
    total_fees: round2(funderFees + Number(platformFee || 0)),
  };
};

/**
 * The repayment bands for one offer.
 *
 *   interestBase = advance + fees   if fees accrue, else advance
 *   feeAddon     = 0                if fees accrue, else fees
 *   amount(n)    = interestBase * (1 + rate/100 * endMonth/12) + feeAddon
 *
 * Simple interest, never compounded. The schedule ends at the first band to
 * reach the cap, or at the term, whichever comes first; that band carries
 * `to: null` and reads "any date after". `is_cap` says the CAP ended it,
 * `is_terminal` only says it was last.
 */
export const computeBands = ({
  advance, fees, annualizedRate, maximumCap, maxTermMonths,
  accrualIntervalMonths, feesAccrueInterest, fundingDate,
}) => {
  if (!VALID_INTERVALS.includes(accrualIntervalMonths)) {
    throw new Error(
      `accrualIntervalMonths must be one of ${VALID_INTERVALS.join(', ')}, `
      + `got ${accrualIntervalMonths}`,
    );
  }
  if (!maxTermMonths || Number(maxTermMonths) < 1) {
    throw new Error(`maxTermMonths must be at least 1, got ${maxTermMonths}`);
  }
  if (!addCalendarMonths(fundingDate, 0)) {
    throw new Error(`fundingDate must be a YYYY-MM-DD date, got ${fundingDate}`);
  }

  const term = Number(maxTermMonths);
  const interestBase = feesAccrueInterest ? advance + fees : advance;
  const feeAddon = feesAccrueInterest ? 0 : fees;

  const bands = [];
  let n = 1;

  for (;;) {
    const startMonth = (n - 1) * accrualIntervalMonths;
    // Clamp rather than overshoot: a 50-month term with 6-month bands ends
    // with a two-month band, not one running past the term to 54.
    const endMonth = Math.min(n * accrualIntervalMonths, term);

    let amount = round2(
      interestBase * (1 + (annualizedRate / 100) * (endMonth / 12)) + feeAddon,
    );

    const isCap = maximumCap > 0 && amount >= maximumCap;
    if (isCap) amount = round2(maximumCap);
    const isTerminal = isCap || endMonth >= term;

    bands.push({
      from_month: startMonth,
      to_month: endMonth,
      from: addCalendarMonths(fundingDate, startMonth),
      to: isTerminal ? null : previousDay(addCalendarMonths(fundingDate, endMonth)),
      amount,
      is_cap: isCap,
      is_terminal: isTerminal,
    });

    if (isTerminal) break;
    n += 1;
  }

  return bands;
};
